import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  Coins,
  Receipt,
  Download,
  FileText,
  ArrowRight,
  MapPin,
  Phone,
  Building2,
  Globe,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const REVENUE = [
  {
    label: "AAMBL Franchise Registration Fees",
    sub: "10-Week Men's League",
    amount: 38500,
  },
  {
    label: "WAABL Franchise Registration Fees",
    sub: "10-Week Women's League",
    amount: 34000,
  },
  {
    label: "Title & Apparel Corporate Partners",
    sub: "Sponsorship Commitments",
    amount: 25000,
  },
  {
    label: "General Gate Admissions & Ticketing",
    sub: "Regular Season Schedule",
    amount: 22400,
  },
  {
    label: "2027 Men's Basketball Showcase",
    sub: "Registrations & Media Gates",
    amount: 19500,
  },
  {
    label: "2027 Women's Basketball Showcase",
    sub: "Registrations & Media Gates",
    amount: 18200,
  },
  {
    label: "Integrated Media Rights & Live-Stream Sponsorships",
    sub: "Dynamic Broadcast Partners",
    amount: 15000,
  },
];

const OPERATIONAL = [
  { label: "Core Professional Management Staff Payroll", amount: 42000 },
  { label: "AAMBL & WAABL 10-Week Facility Rentals & Court Operations", amount: 24000 },
  { label: "Operational Support Structure & Intern Payroll Stipends", amount: 14500 },
  {
    label: "League Play Athletic Gear (100 Men's & Women's Full Uniform Sets)",
    amount: 12500,
  },
  {
    label: "2027 Showcase Venue Infrastructure & Technical Setup",
    amount: 11500,
  },
  { label: "Sanctioned Referees, Statistical Personnel & Event Arbiters", amount: 9600 },
  { label: "Digital Advertising Campaigns & Broadcast Media Logistics", amount: 8200 },
  {
    label: "Corporate Portal Development, Website Maintenance & Digital Hosting",
    amount: 3500,
  },
  {
    label: "Localized Event Materials, Full-Scale Posters & Merchandising Prints",
    amount: 2400,
  },
];

const EQUITY = [
  {
    label: "Mandated Association Owners' Management Fee Allocation",
    amount: 6000,
  },
  {
    label: "Partner Contractual Distribution — Tranche Alpha (Primary)",
    amount: 3500,
  },
  {
    label: "Partner Contractual Distribution — Tranche Beta (Secondary)",
    amount: 2500,
  },
  {
    label: "Partner Contractual Distribution — Tranche Gamma (Associate)",
    amount: 1000,
  },
];

const PRIORITIES = [
  {
    title: "Unified Operations",
    body: "Integrated 2027 Men's & Women's Showcases with 10-week AAMBL and WAABL regular-season operations under one operational design.",
  },
  {
    title: "Market Visibility",
    body: "Strategic spend across digital media, high-production web deployment, localized poster runs, and comprehensive athletic equipment.",
  },
  {
    title: "Revenue Generation",
    body: "Projections leverage historical corporate sponsorship tracks and standard ticket pricing across both leagues.",
  },
  {
    title: "Cost Management",
    body: "Operational expenses managed via direct platform delivery metrics, strict accountability, and dual-tier payroll architecture.",
  },
  {
    title: "Uniform Sourcing",
    body: "Manufacturing & bulk importing of 100 premium, dual-set customized jerseys and shorts for AAMBL & WAABL play.",
  },
  {
    title: "Marketing Integration",
    body: "Dynamic digital ads, specialized web development, and print campaigns built to convert fans and stabilize gate revenue.",
  },
];

const fmt = (n) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  });

const totalRevenue = REVENUE.reduce((a, b) => a + b.amount, 0);
const totalOperational = OPERATIONAL.reduce((a, b) => a + b.amount, 0);
const totalEquity = EQUITY.reduce((a, b) => a + b.amount, 0);
const totalExpenses = totalOperational + totalEquity;
const netSurplus = totalRevenue - totalExpenses;

const Row = ({ item, total, color = "amber" }) => {
  const pct = Math.min(100, Math.round((item.amount / total) * 100));
  const bar =
    color === "amber"
      ? "bg-amber-400"
      : color === "red"
      ? "bg-red-500"
      : "bg-emerald-500";
  return (
    <div className="border-b border-neutral-900 last:border-b-0 px-5 py-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-sm text-white leading-snug">{item.label}</div>
          {item.sub && (
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mt-1">
              {item.sub}
            </div>
          )}
        </div>
        <div className="font-display text-xl text-white shrink-0 tabular-nums">
          {fmt(item.amount)}
        </div>
      </div>
      <div className="mt-3 h-1 bg-neutral-900 overflow-hidden">
        <div className={`${bar} h-full`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const BudgetPage = () => {
  return (
    <div className="App bg-black text-white min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-12 md:pb-16 border-b border-neutral-900 bg-gradient-to-b from-[#0a0a0a] to-black overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-amber-500/10 blur-[140px] pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-[140px] pointer-events-none" />

        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.45em] text-amber-400 mb-6">
            <span className="w-8 h-px bg-amber-400" />
            <Wallet size={14} />
            <span>Fiscal Year 2026 / 2027</span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            data-testid="budget-title"
            className="font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.88] text-white"
          >
            Budget &
            <br />
            <span className="text-amber-400">forecast.</span>
          </motion.h1>
          <p className="text-neutral-400 max-w-xl mt-6 font-light leading-relaxed">
            Public-facing summary of the AfroAm Basketball Association's
            2026–2027 operations cycle — covering AAMBL & WAABL league play, the
            2027 Showcases, sponsorship, payroll, and association equity.
          </p>

          <div className="flex flex-wrap gap-3 mt-8">
            <a
              href="/docs/AfroAm_Budget_2026-2027.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="budget-download"
              className="inline-flex items-center gap-2 bg-amber-400 text-black px-7 py-4 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors"
            >
              <Download size={14} />
              Download Full Report
            </a>
            <a
              href="/docs/AfroAm_Budget_2026-2027.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="budget-view"
              className="inline-flex items-center gap-2 border border-white text-white px-7 py-4 text-xs uppercase tracking-[0.28em] font-bold hover:bg-white hover:text-black transition-colors"
            >
              <FileText size={14} />
              View PDF
            </a>
          </div>
        </div>
      </section>

      {/* Top-line totals */}
      <section className="border-b border-neutral-900 bg-black">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-neutral-900">
          <div className="py-10 px-2 md:px-8" data-testid="kpi-revenue">
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-emerald-400 mb-3">
              <TrendingUp size={14} />
              Total Projected Revenue
            </div>
            <div className="font-display text-5xl md:text-6xl text-white tabular-nums">
              {fmt(totalRevenue)}
            </div>
            <div className="text-xs text-neutral-500 mt-2">
              Across 7 revenue tracks
            </div>
          </div>
          <div className="py-10 px-2 md:px-8" data-testid="kpi-expenses">
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-red-400 mb-3">
              <TrendingDown size={14} />
              Total Projected Expenses
            </div>
            <div className="font-display text-5xl md:text-6xl text-white tabular-nums">
              {fmt(totalExpenses)}
            </div>
            <div className="text-xs text-neutral-500 mt-2">
              Operations · Equity · Distributions
            </div>
          </div>
          <div className="py-10 px-2 md:px-8" data-testid="kpi-surplus">
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-amber-400 mb-3">
              <Coins size={14} />
              Forecasted Net Surplus
            </div>
            <div className="font-display text-5xl md:text-6xl text-amber-400 tabular-nums">
              {fmt(netSurplus)}
            </div>
            <div className="text-xs text-neutral-500 mt-2">
              Retained association surplus
            </div>
          </div>
        </div>
      </section>

      {/* Revenue + Expenses */}
      <section className="py-16 md:py-24 border-b border-neutral-900">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Revenue */}
          <div data-testid="revenue-section">
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.4em] text-emerald-400 mb-5">
              <span className="w-8 h-px bg-emerald-500" />
              Revenue Streams
            </div>
            <h2 className="font-display uppercase text-3xl md:text-5xl text-white leading-[0.95]">
              How we earn.
            </h2>
            <div className="mt-8 border border-neutral-900">
              {REVENUE.map((r) => (
                <Row key={r.label} item={r} total={totalRevenue} color="emerald" />
              ))}
              <div className="flex items-center justify-between px-5 py-4 bg-[#0a0a0a]">
                <div className="text-[10px] uppercase tracking-[0.35em] text-emerald-400">
                  Total Revenue
                </div>
                <div className="font-display text-2xl text-white tabular-nums">
                  {fmt(totalRevenue)}
                </div>
              </div>
            </div>
          </div>

          {/* Expenses */}
          <div data-testid="expense-section">
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.4em] text-red-400 mb-5">
              <span className="w-8 h-px bg-red-500" />
              Operational Expenditures
            </div>
            <h2 className="font-display uppercase text-3xl md:text-5xl text-white leading-[0.95]">
              How we deliver.
            </h2>

            <div className="mt-8 border border-neutral-900">
              {OPERATIONAL.map((r) => (
                <Row key={r.label} item={r} total={totalOperational} color="red" />
              ))}
              <div className="flex items-center justify-between px-5 py-4 bg-[#0a0a0a]">
                <div className="text-[10px] uppercase tracking-[0.35em] text-red-400">
                  Subtotal · Operations
                </div>
                <div className="font-display text-2xl text-white tabular-nums">
                  {fmt(totalOperational)}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.4em] text-amber-400 mt-12 mb-5">
              <span className="w-8 h-px bg-amber-400" />
              Equity & Partner Distributions
            </div>
            <div className="border border-neutral-900">
              {EQUITY.map((r) => (
                <Row key={r.label} item={r} total={totalEquity} color="amber" />
              ))}
              <div className="flex items-center justify-between px-5 py-4 bg-[#0a0a0a]">
                <div className="text-[10px] uppercase tracking-[0.35em] text-amber-400">
                  Subtotal · Equity
                </div>
                <div className="font-display text-2xl text-white tabular-nums">
                  {fmt(totalEquity)}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between px-5 py-5 mt-6 border border-neutral-700 bg-[#0a0a0a]">
              <div className="text-[10px] uppercase tracking-[0.35em] text-white">
                <Receipt size={14} className="inline mr-2 -mt-1" />
                Total Expenses
              </div>
              <div className="font-display text-3xl text-white tabular-nums">
                {fmt(totalExpenses)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic priorities */}
      <section className="py-16 md:py-24 border-b border-neutral-900 bg-[#070707]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-5">
            <span className="w-8 h-px bg-amber-400" />
            Strategic Priorities
          </div>
          <h2 className="font-display uppercase text-3xl md:text-5xl lg:text-6xl text-white leading-[0.95] max-w-3xl">
            How the dollars
            <br />
            <span className="text-amber-400">go to work.</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-900 border border-neutral-900 mt-10">
            {PRIORITIES.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.5 }}
                className="bg-black p-7"
                data-testid={`priority-${i}`}
              >
                <div className="font-display text-3xl text-amber-400 leading-none">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-display uppercase text-2xl text-white mt-4 leading-tight">
                  {p.title}
                </h3>
                <p className="text-sm text-neutral-400 font-light leading-relaxed mt-3">
                  {p.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Document + Contact */}
      <section className="py-16 md:py-24 bg-black">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Embedded PDF preview */}
          <div className="lg:col-span-7" data-testid="pdf-preview">
            <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-5">
              — Full Report
            </div>
            <h2 className="font-display uppercase text-3xl md:text-5xl text-white leading-[0.95] mb-6">
              Read the full forecast.
            </h2>
            <div className="relative border border-amber-400/30 bg-[#050505] aspect-[8.5/11] md:aspect-[8.5/8] overflow-hidden">
              <span className="absolute -top-2 -left-2 w-6 h-6 border-t-2 border-l-2 border-amber-400 z-10" />
              <span className="absolute -top-2 -right-2 w-6 h-6 border-t-2 border-r-2 border-amber-400 z-10" />
              <span className="absolute -bottom-2 -left-2 w-6 h-6 border-b-2 border-l-2 border-amber-400 z-10" />
              <span className="absolute -bottom-2 -right-2 w-6 h-6 border-b-2 border-r-2 border-amber-400 z-10" />
              <iframe
                src="/docs/AfroAm_Budget_2026-2027.pdf#view=FitH"
                title="AfroAm Basketball Budget 2026-2027"
                className="w-full h-full bg-white"
              />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3 justify-between text-[10px] uppercase tracking-[0.3em] text-neutral-500">
              <span>AfroAm Basketball Association · Internal Forecast</span>
              <a
                href="/docs/AfroAm_Budget_2026-2027.pdf"
                download="AfroAm_Budget_2026-2027.pdf"
                data-testid="pdf-direct-download"
                className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300"
              >
                <Download size={12} />
                Direct Download
              </a>
            </div>
          </div>

          {/* Contact card */}
          <div className="lg:col-span-5">
            <div className="border border-neutral-900 bg-[#0a0a0a] p-7 md:p-9 sticky top-28">
              <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-4">
                — Filed By
              </div>
              <h3 className="font-display uppercase text-2xl md:text-3xl text-white leading-tight">
                AfroAm Basketball
                <br />
                Association
              </h3>
              <ul className="mt-6 space-y-4 text-sm text-neutral-300">
                <li className="flex items-start gap-3">
                  <Building2 size={14} className="text-amber-400 mt-1 shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-0.5">
                      Home Headquarters
                    </div>
                    Las Vegas, Nevada
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Building2 size={14} className="text-amber-400 mt-1 shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-0.5">
                      Southwest Office
                    </div>
                    708 Main Street, Suite 1000
                    <br />
                    Houston, Texas 77002
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Building2 size={14} className="text-amber-400 mt-1 shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-0.5">
                      Pacific Division Office
                    </div>
                    470 Noor Street, Suite B
                    <br />
                    South San Francisco, California 94080
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Phone size={14} className="text-amber-400 mt-1 shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-0.5">
                      Phone
                    </div>
                    <a href="tel:8323173208" className="hover:text-white">
                      (832) 317-3208
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin size={14} className="text-amber-400 mt-1 shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-0.5">
                      Operating Regions
                    </div>
                    Las Vegas · Houston · San Francisco
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Globe size={14} className="text-amber-400 mt-1 shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-0.5">
                      Web
                    </div>
                    <a
                      href="https://afroamhoopsglobal.us"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-white"
                    >
                      afroamhoopsglobal.us
                    </a>
                  </div>
                </li>
              </ul>

              <div className="mt-8 pt-6 border-t border-neutral-900 flex flex-wrap gap-3">
                <Link
                  to="/#contact"
                  data-testid="budget-contact-cta"
                  className="inline-flex items-center gap-2 bg-amber-400 text-black px-5 py-3 text-[10px] uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors"
                >
                  Partner With Us
                  <ArrowRight size={12} />
                </Link>
                <a
                  href="mailto:afroambasketball@gmail.com?subject=Sponsorship%20Inquiry"
                  data-testid="budget-sponsor-cta"
                  className="inline-flex items-center gap-2 border border-white text-white px-5 py-3 text-[10px] uppercase tracking-[0.28em] font-bold hover:bg-white hover:text-black transition-colors"
                >
                  Sponsorship
                </a>
              </div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-600 mt-6">
                Figures are projected based on FY 2026–2027 operations cycle and
                subject to revision.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BudgetPage;
